import { useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Dropzone } from '@/components/ui/dropzone'
import { CacheKeys } from '@/lib/cache'
import { DocumentType } from '@/models/document'
import { uploadDocumentForIndication } from '@/models/insuranceApplication'

/** What each document is called and where the customer got it, in their words. */
const DOCUMENT_COPY: Partial<Record<DocumentType, { title: string; source: string }>> = {
  [DocumentType.LOSS_RUN]: {
    title: 'Upload your claims history',
    source: 'Upload what your current insurer sent you.',
  },
  [DocumentType.IFTA]: {
    title: 'Upload your IFTA filing',
    source: 'Upload your most recent quarterly fuel tax return.',
  },
  [DocumentType.MVR]: {
    title: 'Upload your driver records',
    source: 'Upload the motor vehicle record for each driver.',
  },
}

const NEEDS: Partial<Record<DocumentType, string>> = {
  [DocumentType.LOSS_RUN]: 'your loss runs',
  [DocumentType.IFTA]: 'your IFTA filing',
  [DocumentType.MVR]: 'your driver records',
}

interface UploadCarrierDocumentDialogProps {
  applicationId: string
  /** The offer the carrier is holding; null keeps the dialog closed. */
  indicationId: string | null
  documentType: DocumentType
  carrierName?: string
  onClose: () => void
}

/**
 * Uploads whichever document a carrier stopped short of pricing without. The file is
 * stored on the application and handed to the carrier — which also restarts the work
 * the carrier had abandoned, whether that is polling or the submission itself.
 */
export function UploadCarrierDocumentDialog({
  applicationId,
  indicationId,
  documentType,
  carrierName,
  onClose,
}: UploadCarrierDocumentDialogProps) {
  const queryClient = useQueryClient()
  const [file, setFile] = useState<File | null>(null)
  const [isUploading, setIsUploading] = useState(false)
  const copy = DOCUMENT_COPY[documentType] ?? DOCUMENT_COPY[DocumentType.LOSS_RUN]!

  function handleOpenChange(open: boolean) {
    if (open) return
    setFile(null)
    onClose()
  }

  async function handleUpload() {
    if (!file || !indicationId) return
    setIsUploading(true)
    try {
      const result = await uploadDocumentForIndication(applicationId, indicationId, file, documentType)
      await queryClient.invalidateQueries({ queryKey: [CacheKeys.APPLICATION, applicationId] })
      if (result.handledByStaff) {
        toast.success('Thanks. Cai will take care of sending this to the insurer.')
      } else if (result.sentToCarrier) {
        toast.success('Sent to the carrier — we’ll update this quote when they respond')
      } else {
        // Stored either way, so say what did happen rather than just "failed".
        toast.warning('Saved your file, but the carrier would not take it. Cai will follow up.')
      }
      handleOpenChange(false)
    } catch {
      toast.error('Could not upload that file. Try again.')
    } finally {
      setIsUploading(false)
    }
  }

  return (
    <Dialog open={!!indicationId} onOpenChange={handleOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{copy.title}</DialogTitle>
          <DialogDescription>
            {carrierName ? `${carrierName} needs` : 'This carrier needs'}{' '}
            {NEEDS[documentType] ?? 'this document'} before it can price your quote. {copy.source}
          </DialogDescription>
        </DialogHeader>

        <Dropzone onChange={setFile} accept=".pdf,.png,.jpg,.jpeg,.xlsx,.xls,.csv" isLoading={isUploading} />

        <DialogFooter>
          <Button variant="outline" onClick={() => handleOpenChange(false)} disabled={isUploading}>
            Cancel
          </Button>
          <Button onClick={handleUpload} disabled={!file} loading={isUploading ? 'Uploading…' : undefined}>
            Upload
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
